import { z } from "zod";

// ---------------------------------------------------------------- roles & plans

export const TENANT_ROLES = ["admin", "lead", "reviewer", "interviewer", "client_viewer"] as const;
export type TenantRole = (typeof TENANT_ROLES)[number];

/** What each role may do inside a client workspace. Checked on every request. */
export const ROLE_PERMISSIONS: Record<TenantRole, readonly Permission[]> = {
  admin: [
    "engagement.manage",
    "engagement.view",
    "interview.run",
    "interview.review",
    "interview.verify",
    "report.generate",
    "dashboard.view",
    "dashboard.publish",
    "member.manage",
    "transcript.view_identified",
  ],
  lead: [
    "engagement.manage",
    "engagement.view",
    "interview.run",
    "interview.review",
    "interview.verify",
    "report.generate",
    "dashboard.view",
    "dashboard.publish",
    "transcript.view_identified",
  ],
  reviewer: [
    "engagement.view",
    "interview.review",
    "interview.verify",
    "report.generate",
    "dashboard.view",
    "transcript.view_identified",
  ],
  interviewer: ["engagement.view", "interview.run", "dashboard.view"],
  /** Client sponsors: verified, anonymised, published content only. */
  client_viewer: ["dashboard.view"],
};

export type Permission =
  | "engagement.manage"
  | "engagement.view"
  | "interview.run"
  | "interview.review"
  | "interview.verify"
  | "report.generate"
  | "dashboard.view"
  | "dashboard.publish"
  | "member.manage"
  | "transcript.view_identified";

export const PLANS = ["free", "field", "professional", "enterprise"] as const;
export type Plan = (typeof PLANS)[number];

/** Plans differ in volume and features, never in speech, translation or AI quality. */
export const PLAN_LIMITS: Record<Plan, { interviewSeconds: number | null; label: string }> = {
  free: { interviewSeconds: 5 * 60, label: "Free — 5 interview minutes" },
  field: { interviewSeconds: null, label: "Field" },
  professional: { interviewSeconds: null, label: "Professional" },
  enterprise: { interviewSeconds: null, label: "Enterprise" },
};

// ---------------------------------------------------------------- statuses

export const INTERVIEW_STATUSES = [
  "planned",
  "live",
  "processing",
  "draft",
  "reviewed",
  "verified",
  "cancelled",
  "failed",
] as const;
export type InterviewStatus = (typeof INTERVIEW_STATUSES)[number];

export const PHASES = ["mobilise", "discover", "diagnose", "design", "deliver"] as const;
export type Phase = (typeof PHASES)[number];

export const STAKEHOLDER_TYPES = [
  "employee",
  "manager",
  "farmer",
  "supplier",
  "customer",
  "partner",
  "other",
] as const;

export const FINDING_KINDS = ["takeaway", "gap", "suggestion", "risk", "quick_win"] as const;
export type FindingKind = (typeof FINDING_KINDS)[number];

export const GAP_CATEGORIES = [
  "delay",
  "handoff",
  "rework",
  "duplicate_work",
  "workaround",
  "missing_control",
  "compliance",
  "data_quality",
  "capacity",
  "technology",
  "policy",
  "communication",
  "other",
] as const;

export const SEVERITIES = ["low", "medium", "high", "critical"] as const;
export type Severity = (typeof SEVERITIES)[number];

export const REVIEW_STATUSES = ["pending", "accepted", "edited", "rejected"] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

export const STATEMENT_TYPES = ["activity", "pain_point", "workaround", "dependency", "idea", "context"] as const;

// ---------------------------------------------------------------- profile fields

export const STANDARD_PROFILE_FIELDS = [
  "department",
  "designation",
  "location",
  "reportingLine",
  "teamSize",
  "experienceYears",
  "stakeholderType",
] as const;

export const ProfileFieldDef = z.object({
  key: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,40}$/),
  label: z.string().min(1).max(80),
  type: z.enum(["text", "select", "number"]),
  options: z.array(z.string().min(1).max(80)).max(100).optional(),
  required: z.boolean().default(false),
});
export type ProfileFieldDef = z.infer<typeof ProfileFieldDef>;

export const IntervieweeProfile = z.object({
  department: z.string().max(120).optional(),
  designation: z.string().max(120).optional(),
  location: z.string().max(120).optional(),
  reportingLine: z.string().max(120).optional(),
  teamSize: z.coerce.number().int().min(0).max(100000).optional(),
  experienceYears: z.coerce.number().min(0).max(80).optional(),
  stakeholderType: z.enum(STAKEHOLDER_TYPES).optional(),
  custom: z.record(z.string(), z.union([z.string().max(200), z.number()])).default({}),
});
export type IntervieweeProfile = z.infer<typeof IntervieweeProfile>;

// ---------------------------------------------------------------- API inputs

const langCode = z.string().regex(/^[a-z]{2}-[A-Z]{2}$/);

export const CreateTenantInput = z.object({
  name: z.string().min(2).max(120),
  slug: z
    .string()
    .regex(/^[a-z][a-z0-9-]{2,30}$/, "lowercase letters, digits and hyphens, 3–31 characters"),
  plan: z.enum(PLANS).default("field"),
  region: z.string().min(2).max(40).default("us"),
});

export const CreateEngagementInput = z.object({
  name: z.string().min(2).max(160),
  clientUnit: z.string().max(160).optional(),
  country: z.string().length(2).toUpperCase(),
  phase: z.enum(PHASES).default("discover"),
  interviewerLanguages: z.array(langCode).min(1).max(8),
  intervieweeLanguages: z.array(langCode).min(1).max(8),
  reportLanguage: langCode.default("en-US"),
  anonymiseNames: z.boolean().default(true),
  profileFields: z.array(ProfileFieldDef).max(30).default([]),
  focusModules: z.array(z.string().max(60)).max(10).default([]),
  targetInterviews: z.coerce.number().int().min(0).max(10000).optional(),
});
export type CreateEngagementInput = z.infer<typeof CreateEngagementInput>;
export const UpdateEngagementInput = CreateEngagementInput.partial().extend({
  publishedToClient: z.boolean().optional(),
});

export const PlanItemInput = z.object({
  department: z.string().min(1).max(120),
  location: z.string().min(1).max(120),
  designation: z.string().max(120).optional(),
  stakeholderType: z.enum(STAKEHOLDER_TYPES).optional(),
  targetCount: z.coerce.number().int().min(1).max(1000),
  notes: z.string().max(1000).optional(),
});

export const IntervieweeInput = z.object({
  displayName: z.string().max(160).optional(),
  language: langCode,
  profile: IntervieweeProfile,
  planItemId: z.string().uuid().optional(),
});

export const QuestionSetInput = z.object({
  name: z.string().min(1).max(160),
  sourceLang: langCode,
  stakeholderTypes: z.array(z.enum(STAKEHOLDER_TYPES)).default([]),
  moduleKey: z.string().max(60).optional(),
});

export const QuestionInput = z.object({
  topic: z.string().min(1).max(160),
  text: z.string().min(3).max(1000),
  probes: z.array(z.string().max(500)).max(10).default([]),
  order: z.coerce.number().int().min(0).max(10000).default(0),
});

export const TranslationInput = z.object({
  lang: langCode,
  text: z.string().min(1).max(1500),
  verified: z.boolean().default(false),
});

export const GlossaryTermInput = z.object({
  sourceLang: langCode,
  targetLang: langCode,
  source: z.string().min(1).max(200),
  target: z.string().min(1).max(200),
  notes: z.string().max(500).optional(),
});

export const INTERVIEW_MODES = ["two_phone", "record"] as const;
export type InterviewMode = (typeof INTERVIEW_MODES)[number];

export const CreateInterviewInput = z.object({
  intervieweeId: z.string().uuid(),
  /** "two_phone": a phone each, live translation. "record": one phone records the room. */
  mode: z.enum(INTERVIEW_MODES).default("two_phone"),
  /** Others taking part in a group conversation (record mode). */
  participantIds: z.array(z.string().uuid()).max(11).default([]),
  interviewerLang: langCode,
  intervieweeLang: langCode,
  questionSetIds: z.array(z.string().uuid()).max(10).default([]),
  scheduledAt: z.string().datetime().optional(),
});

export const TurnEditInput = z.object({
  sourceText: z.string().min(1).max(10000).optional(),
  targetText: z.string().max(10000).optional(),
  addToGlossary: z
    .object({ source: z.string().min(1).max(200), target: z.string().min(1).max(200) })
    .optional(),
});

export const FindingReviewInput = z.object({
  status: z.enum(["accepted", "edited", "rejected"]),
  title: z.string().max(300).optional(),
  detail: z.string().max(4000).optional(),
  severity: z.enum(SEVERITIES).optional(),
});

export const StepReviewInput = z.object({
  status: z.enum(["accepted", "edited", "rejected"]),
  name: z.string().max(200).optional(),
  lane: z.string().max(120).optional(),
  description: z.string().max(2000).optional(),
});

export const InterviewTransitionInput = z.object({
  to: z.enum(["reviewed", "verified", "draft"]),
  note: z.string().max(2000).optional(),
});

// ---------------------------------------------------------------- AI analysis result

const Evidence = z.object({
  turnSeq: z.number().int(),
  quote: z.string().max(600),
});
export type Evidence = z.infer<typeof Evidence>;

const Range = z.object({
  min: z.number().nullable(),
  max: z.number().nullable(),
  unit: z.string().max(30),
});

export const AnalysisResult = z.object({
  summary: z.string().max(4000),
  intervieweeRole: z.string().max(300),
  processSteps: z
    .array(
      z.object({
        order: z.number().int(),
        lane: z.string().max(120),
        name: z.string().max(200),
        description: z.string().max(1000),
        inputs: z.array(z.string().max(200)).max(10).default([]),
        outputs: z.array(z.string().max(200)).max(10).default([]),
        systems: z.array(z.string().max(120)).max(10).default([]),
        duration: Range.nullable().default(null),
        waitTime: Range.nullable().default(null),
        issues: z.array(z.enum(["bottleneck", "rework", "workaround", "duplicate", "manual"])).default([]),
        evidence: z.array(Evidence).max(8),
      }),
    )
    .max(60),
  findings: z
    .array(
      z.object({
        kind: z.enum(FINDING_KINDS),
        title: z.string().max(300),
        detail: z.string().max(2000),
        category: z.enum(GAP_CATEGORIES).nullable().default(null),
        severity: z.enum(SEVERITIES).nullable().default(null),
        relatesToStep: z.number().int().nullable().default(null),
        addressesFinding: z.number().int().nullable().default(null),
        evidence: z.array(Evidence).max(8),
      }),
    )
    .max(80),
  activities: z
    .array(
      z.object({
        activity: z.string().max(200),
        role: z.string().max(120),
        timePerOccurrence: Range.nullable(),
        frequency: z.object({ count: z.number().nullable(), per: z.enum(["day", "week", "month", "season", "year"]) }).nullable(),
        volume: z.string().max(200).nullable(),
        waitTime: Range.nullable(),
        confidence: z.enum(["low", "medium", "high"]),
        evidence: z.array(Evidence).max(5),
      }),
    )
    .max(60),
  statementTags: z
    .array(
      z.object({
        turnSeq: z.number().int(),
        type: z.enum(STATEMENT_TYPES),
        processStep: z.number().int().nullable(),
      }),
    )
    .max(400),
  moduleChecks: z
    .array(
      z.object({
        moduleKey: z.string(),
        itemKey: z.string(),
        status: z.enum(["ok", "gap", "partial", "unknown"]),
        note: z.string().max(1000),
        evidence: z.array(Evidence).max(5),
      }),
    )
    .default([]),
  openQuestions: z.array(z.string().max(400)).max(20).default([]),
});
export type AnalysisResult = z.infer<typeof AnalysisResult>;

/** Engagement-level process map produced by consolidating verified interviews. */
export const ProcessMapGraph = z.object({
  title: z.string(),
  lanes: z.array(z.object({ id: z.string(), name: z.string() })),
  steps: z.array(
    z.object({
      id: z.string(),
      laneId: z.string(),
      name: z.string(),
      order: z.number(),
      mentions: z.number().int(),
      confidence: z.enum(["low", "medium", "high"]),
      markings: z.array(z.enum(["match", "overlap", "gap", "extra", "conflict", "bottleneck"])),
      note: z.string().default(""),
      evidence: z.array(z.object({ interviewId: z.string(), turnSeq: z.number().int(), quote: z.string() })).max(12),
    }),
  ),
  edges: z.array(z.object({ from: z.string(), to: z.string(), label: z.string().optional() })),
  variations: z.array(z.object({ stepId: z.string(), description: z.string() })).default([]),
});
export type ProcessMapGraph = z.infer<typeof ProcessMapGraph>;

/** Consolidation output: the merged map plus issues that recur across interviews. */
export const ConsolidationResult = z.object({
  map: ProcessMapGraph,
  themes: z
    .array(
      z.object({
        title: z.string().max(300),
        kind: z.enum(["gap", "risk", "quick_win"]),
        severity: z.enum(SEVERITIES).nullable(),
        detail: z.string().max(1500).default(""),
        interviewIds: z.array(z.string()).max(500),
      }),
    )
    .max(60)
    .default([]),
});
export type ConsolidationResult = z.infer<typeof ConsolidationResult>;

/** A follow-up the interviewer phone may suggest during the conversation. */
export const LiveSuggestion = z.object({
  kind: z.enum(["follow_up", "missing_number", "risk", "none"]),
  text: z.string().max(300),
});
